/**
 * Vincent #164/#165 (Eric 2026-05-24) — Vue apprenant des Parcours
 *
 * Cette page affiche les parcours assignés à l'utilisateur courant
 * (filtrés par le backend selon son rôle/groupes), avec :
 *   - barre de progression "X / Y vidéos vues"
 *   - liste ordonnée des vidéos
 *   - lien direct vers chaque vidéo
 *   - badge "Terminé" si 100%
 *
 * Route : /dashboard/onboarding (rôles : tous) + /dashboard/learning-path (R_FROM_FORME)
 * Différentié du backend par `is_designer` : si false, l'utilisateur voit cette UI.
 */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { CheckCircle, Path, PlayCircle, Trophy, Heart, FileText, CaretDown, CaretRight } from "@phosphor-icons/react";
import PathwayLearnerModal from "../components/pathways/PathwayLearnerModal";
import { usePathwayQuizEligibility, pathwayQuizButtonClasses } from "../lib/usePathwayQuizEligibility";

const L = (lang, fr, en) => (lang === "fr" ? fr : en);

/**
 * Vincent XLSX 2026-07-13 21h Row 451 — Bouton "Faire le quiz du parcours"
 * coloré selon l'éligibilité de l'utilisateur.
 */
function PathwayQuizButton({ pathwayId, lang, testId }) {
  const { loading, eligibility } = usePathwayQuizEligibility(pathwayId);
  if (loading || !eligibility) {
    return (
      <div className="mt-3 pt-3 border-t border-zinc-100 flex justify-end">
        <span className="text-[11px] text-zinc-400 italic">…</span>
      </div>
    );
  }
  const isLocked = eligibility.state === "locked";
  const label = eligibility.state === "passed"
    ? `✓ ${L(lang, "Quiz du parcours réussi", "Pathway quiz passed")}`
    : `🎯 ${L(lang, "Faire le quiz du parcours", "Take the pathway quiz")}`;
  const scoreSuffix = eligibility.pathway_quiz_best_score != null
    ? ` (${Math.round(eligibility.pathway_quiz_best_score)}%)`
    : "";

  if (isLocked) {
    return (
      <div className="mt-3 pt-3 border-t border-zinc-100 flex justify-end">
        <button
          type="button"
          onClick={() => toast.info(eligibility.reason_fr, { duration: 6000 })}
          className={pathwayQuizButtonClasses("locked")}
          data-testid={testId}
          title={eligibility.reason_fr}
        >
          🔒 {L(lang, "Quiz du parcours verrouillé", "Pathway quiz locked")}
        </button>
      </div>
    );
  }
  return (
    <div className="mt-3 pt-3 border-t border-zinc-100 flex justify-end">
      <Link
        to={`/dashboard/quiz?pathway_id=${encodeURIComponent(pathwayId)}&num_questions=7&auto=1`}
        className={pathwayQuizButtonClasses(eligibility.state)}
        data-testid={testId}
        title={eligibility.reason_fr}
      >
        {label}{scoreSuffix}
      </Link>
    </div>
  );
}

const API = process.env.REACT_APP_BACKEND_URL;
function L2(lang, fr, en) { return lang === "fr" ? fr : en; }

export default function LearnerPathwaysView({ pathwayType }) {
  const { lang } = useLang();
  const { user, refreshUser } = useAuth();
  const [pathways, setPathways] = useState([]);
  const [progressByPathway, setProgressByPathway] = useState({});
  const [loading, setLoading] = useState(true);
  // Vincent #262 (2026-06-11) — Clic sur la tuile = ouvre la fenêtre du parcours
  const [opened, setOpened] = useState(null);
  // Vincent #390 (Eric 2026-06-29 SPEC STRICK) — best score quiz par parcours.
  const [bestScoreByPathway, setBestScoreByPathway] = useState({});

  // Vincent B568 (Eric 2026-07-27) — Coeurs favoris directement dans les
  // parcours d'intégration/apprentissage, à gauche du nom de chaque vidéo.
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  // Vincent B623 (Eric 2026-07-30 SPEC STRICK — PROD) — Collapse/expand par
  // parcours + persistance du « dernier consulté ».
  // Vincent B631 (Eric 2026-07-31 SPEC STRICK PROD) — Passage d'un état
  // « dernier consulté » (1 seul ID) à une VRAIE mémorisation multi-ouverts.
  // Chaque parcours peut être ouvert ou fermé indépendamment. La préférence
  // stocke désormais un TABLEAU d'IDs ouverts (array). Défaut nouvel
  // utilisateur (préférence absente/null) : le 1er parcours par ordre
  // alphabétique est ouvert, tous les autres fermés.
  const PREF_KEY = `pathway_open_ids_${pathwayType}`;
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/favorites`, { withCredentials: true });
        setFavoriteIds(new Set(data.filter(f => f.item_type === "video").map(f => f.item_id)));
      } catch { /* silencieux */ }
    })();
  }, []);

  const toggleFavorite = async (e, videoId) => {
    e.preventDefault();
    e.stopPropagation();
    const wasFavorite = favoriteIds.has(videoId);
    setFavoriteIds(prev => {
      const s = new Set(prev);
      if (wasFavorite) s.delete(videoId); else s.add(videoId);
      return s;
    });
    try {
      if (wasFavorite) {
        await axios.delete(`${API}/api/favorites/video/${videoId}`, { withCredentials: true, _allowRetry: true });
      } else {
        await axios.post(`${API}/api/favorites`, { type: "video", item_id: videoId }, { withCredentials: true, _allowRetry: true });
      }
    } catch (err) {
      setFavoriteIds(prev => {
        const s = new Set(prev);
        if (wasFavorite) s.add(videoId); else s.delete(videoId);
        return s;
      });
      const msg = err?.response?.data?.detail || err?._friendlyMessage || "Impossible de mettre à jour le favori.";
      toast.error(msg);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/pathways?pathway_type=${pathwayType}`, { withCredentials: true });
        const items = r.data.pathways || [];
        setPathways(items);
        // Fetch progress for each in parallel
        const progress = {};
        await Promise.all(items.map(async (p) => {
          try {
            const pr = await axios.get(`${API}/api/pathways/${p.id}/progress`, { withCredentials: true });
            progress[p.id] = pr.data;
          } catch (_) { progress[p.id] = { completed: 0, total: (p.video_ids || []).length, percent: 0 }; }
        }));
        setProgressByPathway(progress);
        // Vincent #390 — Calcul du meilleur score par parcours via /quiz/history.
        // Vincent #390 (À REFAIRE 2026-07-01) — Les quiz effectués AVANT le
        // déploiement iter130 n'ont pas de `pathway_id` persisté. On dérive
        // donc aussi le pathway_id via le `video_id` du résultat de quiz :
        // si un quiz porte sur une vidéo qui appartient à un ou plusieurs
        // parcours, on attribue son score à CHACUN de ces parcours (une même
        // vidéo peut être incluse dans plusieurs parcours et le score compte
        // pour tous — sémantique retenue pour maximiser le feedback learner).
        try {
          const h = await axios.get(`${API}/api/quiz/history`, { withCredentials: true });
          const historyArr = Array.isArray(h.data) ? h.data : (h.data?.history || h.data?.results || []);
          // Table video_id → [pathway_id]
          const videoToPathways = {};
          for (const p of items) {
            for (const vid of (p.video_ids || [])) {
              (videoToPathways[vid] = videoToPathways[vid] || []).push(p.id);
            }
          }
          const best = {};
          const updateBest = (pid, sc) => {
            if (best[pid] === undefined || sc > best[pid]) best[pid] = sc;
          };
          for (const row of historyArr) {
            const sc = Number(row.score_percentage || 0);
            // 1) explicit pathway_id
            if (row.pathway_id) {
              updateBest(row.pathway_id, sc);
              continue;
            }
            // 2) derived from video_id
            const vid = row.video_id;
            if (vid && videoToPathways[vid]) {
              for (const pid of videoToPathways[vid]) updateBest(pid, sc);
            }
          }
          setBestScoreByPathway(best);
        } catch (_) { /* non-bloquant */ }
      } finally {
        setLoading(false);
      }
    })();
  }, [pathwayType]);

  const title = pathwayType === "onboarding"
    ? L(lang, "Mes parcours d'intégration", "My onboarding pathways")
    : L(lang, "Mes parcours d'apprentissage", "My learning pathways");

  // Vincent B623 (Eric 2026-07-30 SPEC STRICK PROD) — Tri A→Z stable
  // (utilisé pour l'affichage ET pour trouver « le 1er alphabétique »
  // en absence de dernière préférence).
  const sortedPathways = useMemo(
    () => [...pathways].sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" })),
    [pathways]
  );

  // Détermine l'état initial d'expansion :
  //  1. Si l'utilisateur a une préférence `pathway_open_ids_{type}` (array), on
  //     ré-ouvre exactement les parcours dont l'ID est encore présent en base.
  //     Cas particulier : array vide = « tout fermé » (choix utilisateur).
  //  2. Sinon (nouvel utilisateur — préférence absente/null/non-array), on
  //     ouvre le 1er parcours par ordre alphabétique. Cas conservé pour
  //     rétro-compatibilité avec l'ancienne clé `pathway_last_opened_*` en
  //     fallback.
  useEffect(() => {
    if (initialized || loading || sortedPathways.length === 0) return;
    const raw = user?.preferences?.[PREF_KEY];
    const legacyLastId = user?.preferences?.[`pathway_last_opened_${pathwayType}`];
    const validIds = new Set(sortedPathways.map((p) => p.id));
    let next;
    if (Array.isArray(raw)) {
      // Array persisté (nouveau schéma B631) — respecte y compris vide
      next = new Set(raw.filter((id) => validIds.has(id)));
    } else if (typeof legacyLastId === "string" && validIds.has(legacyLastId)) {
      // Fallback ancien schéma (B623 legacy) — 1 seul ID
      next = new Set([legacyLastId]);
    } else {
      // Nouvel utilisateur → 1er alphabétique ouvert
      next = new Set([sortedPathways[0].id]);
    }
    setExpandedIds(next);
    setInitialized(true);
  }, [sortedPathways, loading, initialized, user?.preferences, PREF_KEY, pathwayType]);

  const toggleExpand = async (pathwayId, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    // Calcule le nouvel ensemble d'ouverts + persiste l'array complet.
    const wasOpen = expandedIds.has(pathwayId);
    const nextSet = new Set(expandedIds);
    if (wasOpen) nextSet.delete(pathwayId); else nextSet.add(pathwayId);
    setExpandedIds(nextSet);
    // Persist en tableau (ordre alphabétique pour idempotence côté serveur).
    const nextArray = [...nextSet].sort();
    try {
      await axios.patch(
        `${API}/api/auth/me/preferences`,
        { [PREF_KEY]: nextArray },
        { withCredentials: true }
      );
      if (typeof refreshUser === "function") await refreshUser();
    } catch { /* silencieux — l'UI reste réactive même sans persist */ }
  };

  if (loading) return <div className="p-6 text-center text-zinc-400">{L(lang, "Chargement…", "Loading…")}</div>;

  if (pathways.length === 0) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="learner-pathways-empty">
        <h1 className="text-3xl font-bold text-zinc-900 flex items-center gap-2 mb-4">
          <Path size={28} weight="duotone" /> {title}
        </h1>
        <div className="text-center py-16 bg-zinc-50 rounded-xl border-2 border-dashed border-zinc-200">
          <p className="text-zinc-500">{L(lang, "Aucun parcours ne vous a encore été assigné.", "No pathway has been assigned to you yet.")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid={`learner-pathways-${pathwayType}`}>
      <h1 className="text-3xl font-bold text-zinc-900 flex items-center gap-2 mb-6">
        <Path size={28} weight="duotone" /> {title}
      </h1>
      <ul className="space-y-4">
        {/* Vincent #293 (2026-06-12) — Tri A→Z par défaut (pas de filtre exposé). */}
        {sortedPathways.map((p) => {
          const pr = progressByPathway[p.id] || { completed: 0, total: (p.video_ids || []).length, percent: 0 };
          const done = pr.total > 0 && pr.completed === pr.total;
          const isExpanded = expandedIds.has(p.id);
          return (
            <li
              key={p.id}
              className="bg-white border border-zinc-200 rounded-lg p-4 cursor-pointer hover:border-[#002FA7]/40 hover:shadow-sm transition-all"
              data-testid={`learner-pathway-${p.id}`}
              onClick={(e) => {
                // Vincent #262 (2026-06-11) — Clic sur la tuile (hors liens/quiz)
                // ouvre la FENÊTRE avec la liste des vidéos cliquables + quiz.
                if (e.target.closest("a, button, input")) return;
                setOpened(p);
              }}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {/* Vincent B623 (Eric 2026-07-30 SPEC STRICK PROD) —
                        Triangle collapse/expand à GAUCHE du titre du parcours.
                        Par défaut, seul le dernier parcours consulté est
                        ouvert (persisté via user.preferences.pathway_last_
                        opened_{type}) ; à défaut, le 1er par ordre A→Z.
                        Le triangle est un vrai bouton pour l'accessibilité
                        clavier (Enter / Space). */}
                    <button
                      type="button"
                      onClick={(e) => toggleExpand(p.id, e)}
                      className="p-0.5 -ml-1 rounded hover:bg-zinc-100 text-zinc-500 hover:text-zinc-800 flex-shrink-0 transition-colors"
                      data-testid={`learner-pathway-toggle-${p.id}`}
                      title={isExpanded ? L(lang, "Fermer ce parcours", "Collapse this pathway") : L(lang, "Ouvrir ce parcours", "Expand this pathway")}
                      aria-expanded={isExpanded}
                      aria-controls={`learner-pathway-body-${p.id}`}
                    >
                      {isExpanded
                        ? <CaretDown size={18} weight="bold" />
                        : <CaretRight size={18} weight="bold" />}
                    </button>
                    <h3 className="font-bold text-zinc-900 text-lg">{p.name}</h3>
                    {done && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-teal-100 text-teal-700 text-xs font-bold">
                        <Trophy size={12} weight="fill" /> {L(lang, "Terminé", "Completed")}
                      </span>
                    )}
                  </div>
                  {p.description && <p className="text-sm text-zinc-600">{p.description}</p>}
                </div>
                <div className="text-right" data-testid={`learner-pathway-progress-${p.id}`}>
                  <div className="text-2xl font-bold text-[#002FA7]">{pr.percent}%</div>
                  {/* Vincent B586 (Eric 2026-07-27 20h) — 2 chiffres au lieu
                      d'un : X vues (≥75 %) · Y quiz réussis (≥60 %) sur Z
                      vidéos actives. `pr.watched` et `pr.quiz_passed` sont
                      exposés par /api/pathways/{id}/progress. */}
                  <div className="text-[10px] text-zinc-500 whitespace-nowrap">
                    <span data-testid={`learner-pathway-watched-${p.id}`}>{pr.watched ?? pr.completed ?? 0}</span>
                    <span> {L(lang, "vues", "watched")} · </span>
                    <span data-testid={`learner-pathway-quiz-passed-${p.id}`}>{pr.quiz_passed ?? 0}</span>
                    <span> {L(lang, "quiz", "quiz")}</span>
                    <span> / {pr.total} {L(lang, "vidéos", "videos")}</span>
                  </div>
                  {/* Bogue 584, 2e passe (Eric 2026-06-26) — Un dénominateur
                      qui rétrécit sans explication passe pour un bogue : on
                      dit combien de références ne comptent plus (vidéo
                      supprimée, à la corbeille ou en échec). */}
                  {(pr.excluded_count || 0) > 0 && (
                    <div
                      className="text-[10px] text-amber-700 whitespace-nowrap"
                      data-testid={`learner-pathway-excluded-${p.id}`}
                      title={L(
                        lang,
                        "Ces vidéos ont été supprimées, mises à la corbeille ou sont en échec : elles ne comptent plus dans la progression.",
                        "These videos were deleted, trashed or failed: they no longer count toward progress."
                      )}
                    >
                      {pr.excluded_count} {L(lang, "non comptée(s)", "not counted")}
                    </div>
                  )}
                </div>
              </div>
              {/* Vincent B623 — Corps collapsible (barre progression + liste
                  vidéos + bouton quiz du parcours). Masqué quand isExpanded=false. */}
              {isExpanded && (
                <div id={`learner-pathway-body-${p.id}`} data-testid={`learner-pathway-body-${p.id}`}>
              {/* Progress bar */}
              <div className="h-2 bg-zinc-100 rounded-full overflow-hidden mb-3">
                <div
                  className={`h-full transition-all ${done ? "bg-teal-500" : "bg-[#002FA7]"}`}
                  style={{ width: `${pr.percent}%` }}
                  data-testid={`progress-bar-${p.id}`}
                />
              </div>
              {/* Video list */}
              <ol className="space-y-1.5 mt-2">
                {(p.videos || [])
                  // Vincent B587 (Eric 2026-07-28 SPEC STRICK) — Filtre
                  // défensif : ne rend jamais une vidéo dont ni `title` ni
                  // `original_filename` n'existe (fantôme). La migration 012
                  // + la cascade DELETE (routers/videos.py) nettoient
                  // normalement ces cas, ce filtre est un last-line
                  // pour éviter tout affichage « (Sans titre) ».
                  .filter((v) => {
                    const derived = (v.original_filename || "").replace(/\.[^.]+$/, "").replace(/_/g, " ").trim();
                    return Boolean((v.title || "").trim() || derived);
                  })
                  .map((v, idx) => {
                  const isDone = (pr.completed_video_ids || []).includes(v.id);
                  // Vincent #317/#318 (Eric 2026-06-17 SPEC STRICK) — Indicateurs % vidéo + check vert + % quiz
                  const vp = (pr.videos_progress || {})[v.id] || {};
                  const watched = vp.watched;
                  const watchPct = vp.watch_pct || 0;
                  const watchSec = vp.watch_seconds || 0;
                  const quizScore = vp.quiz_score;
                  const quizPassed = vp.quiz_passed;
                  const fmtDur = (s) => {
                    if (!s) return "0 s";
                    if (s < 60) return `${s} s`;
                    const m = Math.floor(s / 60);
                    const rs = s % 60;
                    return rs ? `${m} min ${rs} s` : `${m} min`;
                  };
                  return (
                    <li key={v.id} className="flex items-start gap-2">
                      {/* Vincent B568 (Eric 2026-07-27) — Coeur favori à
                          gauche du nom de la vidéo dans le parcours.
                          Vincent B599 (Eric 2026-07-29 SPEC STRICK) — Le ✓
                          de validation est placé SOUS le ♥ dans la même
                          colonne visuelle (flex-col), TOUT à gauche du n°
                          de vidéo (et non à droite). Sur les vidéos non
                          validées, on garde un placeholder pour préserver
                          l'alignement vertical entre les lignes. */}
                      <div className="flex flex-col items-center gap-0.5 mt-1 flex-shrink-0" data-testid={`pathway-fav-check-col-${v.id}`}>
                        <button
                          type="button"
                          onClick={(e) => toggleFavorite(e, v.id)}
                          className={`p-0.5 rounded transition-colors ${favoriteIds.has(v.id) ? "text-rose-500" : "text-zinc-300 hover:text-rose-400"}`}
                          data-testid={`pathway-fav-btn-${v.id}`}
                          title={favoriteIds.has(v.id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                        >
                          <Heart size={14} weight={favoriteIds.has(v.id) ? "fill" : "regular"} />
                        </button>
                        {watched && quizPassed ? (
                          <CheckCircle size={14} weight="fill" className="text-teal-500" data-testid={`learner-pathway-video-${v.id}-watch-check`} />
                        ) : (
                          <span className="h-3.5 w-3.5" aria-hidden="true" data-testid={`learner-pathway-video-${v.id}-watch-check-placeholder`} />
                        )}
                      </div>
                      <Link
                        to={`/dashboard/videos/${v.id}`}
                        className="flex-1 px-2 py-1.5 rounded hover:bg-zinc-50 text-sm group"
                        data-testid={`learner-pathway-video-${v.id}`}
                      >
                        {/* Vincent #317 (Eric 2026-06-22 SPEC STRICK 3e passe) — layout VERTICAL :
                            ligne 1 = titre. ligne 2 (sous le titre) = check vert + durée +
                            (%) + mention "Terminé" si 100%. Pas barré, juste couleur.
                            Vincent B599 (Eric 2026-07-29 SPEC STRICK) — Le ✓ de validation
                            est désormais rendu dans la colonne ♥/✓ à gauche de ce Link.
                            Ici on ne conserve que le PlayCircle indicatif quand la vidéo
                            n'est pas encore validée, à droite du numéro. */}
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[11px] text-zinc-400 w-6 flex-shrink-0">{idx + 1}.</span>
                          {!(watched && quizPassed) && (
                            /* Vincent B622 (Eric 2026-07-30 SPEC STRICK) — Icône
                                distincte pour les documents texte : FileText
                                (feuille) au lieu de PlayCircle (▶). Permet à
                                l'apprenant de repérer d'un coup d'œil les
                                fichiers textuels VS les vidéos dans la liste. */
                            v.is_text_doc
                              ? <FileText size={16} className="text-zinc-400 group-hover:text-[#002FA7] flex-shrink-0" data-testid={`learner-pathway-icon-text-${v.id}`} />
                              : <PlayCircle size={16} className="text-zinc-400 group-hover:text-[#002FA7] flex-shrink-0" data-testid={`learner-pathway-icon-video-${v.id}`} />
                          )}
                          <span className={`flex-1 truncate ${watched && quizPassed ? "text-teal-600 font-semibold" : "text-zinc-800"}`}>
                            {/* Vincent #374 (Eric 2026-06-23 SPEC STRICK) — fallback intelligent */}
                            {v.title || (v.original_filename || "").replace(/\.[^.]+$/, "").replace(/_/g, " ").trim() || L(lang, "(Sans titre)", "(Untitled)")}
                          </span>
                        </div>
                        {/* Vincent #401 + #424 + B593 (Eric 2026-07-28 SPEC STRICK) —
                            Ligne sous le titre : [durée (%)] · [check ≥ seuil vidéo] · [badge « Terminée » si 100 %].
                            B593 : le check ≥ seuil vidéo (ex : ≥ 75 %) apparaît ENTRE le %age et le badge « Terminée »,
                            plus à gauche du texte. */}
                        {(watched || watchSec > 0) && (
                          <div className={`ml-8 mt-0.5 flex items-center gap-1.5 text-[11px] font-semibold ${watched ? "text-teal-600" : "text-zinc-500"}`} data-testid={`learner-pathway-video-${v.id}-watch-pct`}>
                            <span>{fmtDur(watchSec)} ({watchPct}%)</span>
                            {watched && (
                              <CheckCircle size={11} weight="fill" data-testid={`learner-pathway-video-${v.id}-watch-threshold-check`} />
                            )}
                            {watchPct >= 100 && (
                              <span className="px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-800 font-bold text-[10px]" data-testid={`learner-pathway-video-${v.id}-completed-badge`}>
                                {L(lang, "Terminée", "Done")}
                              </span>
                            )}
                          </div>
                        )}
                      </Link>
                      {/* Vincent #318 — Quiz par VIDÉO avec % + check vert si ≥60%.
                          Vincent B615 (Eric 2026-07-29 SPEC STRICK) — Retire le
                          logo 🎯 (« target ») et le remplace par le check vert
                          à GAUCHE du mot « Quiz » quand le quiz est validé.
                          Quand non-validé (ou non-fait) : pas d'icône devant
                          le mot Quiz — le style violet du Tag suffit à indiquer
                          l'état « à faire / à refaire ». */}
                      <Link
                        to={`/dashboard/quiz?video_id=${v.id}&auto=1`}
                        className={`text-[11px] px-2 py-1 rounded border font-semibold whitespace-nowrap inline-flex items-center gap-1 ${
                          quizPassed
                            ? "border-teal-300 bg-teal-50 hover:bg-teal-100 text-teal-700"
                            : "border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-700"
                        }`}
                        data-testid={`learner-pathway-video-quiz-${v.id}`}
                        title={
                          quizScore !== null && quizScore !== undefined
                            ? L(lang, `Quiz réalisé — score ${quizScore}%`, `Quiz done — score ${quizScore}%`)
                            : L(lang, `Quiz sur cette vidéo (≥${pr.quiz_passing_threshold_pct ?? 60}% = validé)`, `Quiz on this video (≥${pr.quiz_passing_threshold_pct ?? 60}% = passed)`)
                        }
                      >
                        {quizPassed && <CheckCircle size={11} weight="fill" data-testid={`learner-pathway-video-${v.id}-quiz-check`} />}
                        Quiz
                        {quizScore !== null && quizScore !== undefined && (
                          <span className="font-bold" data-testid={`learner-pathway-video-${v.id}-quiz-score`}>
                            ({quizScore}%)
                          </span>
                        )}
                        {quizScore === null || quizScore === undefined ? (
                          <span className="font-normal opacity-70">(— %)</span>
                        ) : null}
                      </Link>
                    </li>
                  );
                })}
              </ol>
              {/* Vincent XLSX 2026-07-13 21h Row 451 (Eric 2026-07-14 SPEC STRICK) —
                  Bouton coloré selon l'éligibilité :
                    - ROUGE (locked, non-cliquable + toast informatif) : au moins
                      une vidéo n'a pas ≥ 60 % au quiz de vidéo.
                    - BLEU (eligible) : toutes les vidéos ≥ 60 %, mais quiz
                      général pas encore passé.
                    - VERT (passed) : quiz général réussi ≥ 60 %.
                  Ancien code (best score par calcul frontend) remplacé par
                  l'appel /api/pathways/{id}/quiz-eligibility qui applique la
                  logique côté serveur (source de vérité). */}
              {(p.videos || []).length > 0 && (
                <PathwayQuizButton
                  pathwayId={p.id}
                  lang={lang}
                  testId={`learner-pathway-quiz-${p.id}`}
                />
              )}
              </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Vincent #262 (2026-06-11) — Fenêtre du parcours (vidéos cliquables + quiz) */}
      {opened && (
        <PathwayLearnerModal
          pathway={opened}
          videos={opened.videos || []}
          progress={progressByPathway[opened.id] || null}
          bestQuizScore={bestScoreByPathway[opened.id] !== undefined ? bestScoreByPathway[opened.id] : null}
          lang={lang}
          onClose={() => setOpened(null)}
        />
      )}
    </div>
  );
}
